import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import CompProPanel from "@/components/CompProPanel";
import {
  Users, Crown, CurrencyDollar, Calendar, Flag, Gift, ArrowLeft, ArrowsClockwise,
} from "@phosphor-icons/react";

const CCY_PREFIX = { fjd: "FJ$", usd: "$" };
function fmt(amount, ccy) {
  const pre = CCY_PREFIX[ccy] || `${(ccy || "").toUpperCase()} `;
  return `${pre}${amount.toFixed(2)}`;
}
function sumByCcy(buckets) {
  return Object.entries(buckets || {}).map(([ccy, v]) => fmt(v.amount, ccy)).join(" + ") || "—";
}
function countAll(buckets) {
  return Object.values(buckets || {}).reduce((a, v) => a + v.count, 0);
}

export default function Admin() {
  const [m, setM] = useState(null);
  const [denied, setDenied] = useState(false);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setReloading(true);
    try {
      const r = await api.get("/admin/metrics");
      setM(r.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    } finally { setReloading(false); }
  };
  useEffect(() => { load(); }, []);

  if (denied) {
    return (
      <div className="max-w-md mx-auto py-12">
        <div className="card-default p-8 text-center" data-testid="admin-denied">
          <h1 className="font-heading font-black text-2xl">Admin only</h1>
          <p className="text-sm text-[#0F2537]/70 mt-2">This page is restricted to the Bula Golf operator account.</p>
          <Link to="/dashboard" className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-[#E05D3A]">
            <ArrowLeft size={14} weight="bold" /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }
  if (!m) return <div className="py-10">Loading…</div>;

  return (
    <div className="space-y-7">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">Operator console</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Admin dashboard</h1>
          <p className="text-[#0F2537]/70 mt-1 text-sm">Generated {new Date(m.generated_at).toLocaleString()}</p>
        </div>
        <button onClick={load} disabled={reloading} data-testid="admin-reload" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-xs font-bold disabled:opacity-50">
          <ArrowsClockwise size={14} weight="bold" className={reloading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile testid="kpi-revenue-30d" icon={CurrencyDollar} accent label="Revenue · 30d" value={sumByCcy(m.revenue.last_30d)} sub={`${countAll(m.revenue.last_30d)} paid transactions`} />
        <Tile testid="kpi-revenue-lifetime" icon={CurrencyDollar} label="Revenue · lifetime" value={sumByCcy(m.revenue.lifetime)} sub={`${countAll(m.revenue.lifetime)} paid transactions`} />
        <Tile testid="kpi-active-pro" icon={Crown} accent label="Active Pro" value={m.pro.active} sub={`${m.pro.active_paid} paid · ${m.pro.active_referral} referral`} />
        <Tile testid="kpi-users-30d" icon={Users} label="Users · total" value={m.users.total} sub={`+${m.users.new_30d} new in 30d`} />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile testid="kpi-tee-bookings" icon={Flag} label="Tee bookings · 30d" value={m.bookings.tee_30d} sub={`${m.bookings.tee_lifetime} lifetime`} />
        <Tile testid="kpi-resort-bookings" icon={Calendar} label="Resort bookings · 30d" value={m.bookings.resort_30d} sub={`${m.bookings.resort_lifetime} lifetime`} />
        <Tile testid="kpi-rounds" icon={Flag} label="Rounds played · 30d" value={m.engagement.rounds_30d} sub={`${m.engagement.rounds_lifetime} lifetime`} />
        <Tile testid="kpi-referrals" icon={Gift} accent label="Referrals · 30d" value={m.referrals.last_30d} sub={`${m.referrals.total} lifetime`} />
      </section>

      {Object.keys(m.revenue.refunds || {}).length > 0 && (
        <section className="card-default p-5" data-testid="admin-refunds">
          <span className="label-eyebrow text-red-600">Refunds issued</span>
          <div className="font-heading font-black text-3xl mt-1 text-red-700">{sumByCcy(m.revenue.refunds)}</div>
          <p className="text-xs text-[#0F2537]/60 mt-1">{countAll(m.revenue.refunds)} refunded transaction(s)</p>
        </section>
      )}

      <section className="card-default overflow-hidden" data-testid="admin-recent-tx">
        <div className="px-5 py-4 border-b border-[#E2E0D8] flex items-center justify-between">
          <span className="label-eyebrow">Recent paid transactions</span>
          <span className="text-xs font-bold text-[#0F2537]/50">{m.recent_transactions.length} shown</span>
        </div>
        {m.recent_transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#0F2537]/60">No paid transactions yet. Once Stripe goes live, they'll appear here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-[#0F2537]/55">
                <tr>
                  <th className="text-left px-5 py-3">User</th>
                  <th className="text-left px-2 py-3">Plan</th>
                  <th className="text-right px-2 py-3">Amount</th>
                  <th className="text-right px-5 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {m.recent_transactions.map((t, i) => (
                  <tr key={i} className="border-t border-[#E2E0D8]">
                    <td className="px-5 py-3">
                      <div className="font-bold">{t.user_name}</div>
                      <div className="text-xs text-[#0F2537]/60">{t.user_email}</div>
                    </td>
                    <td className="px-2 py-3 font-bold uppercase">{t.plan}</td>
                    <td className="px-2 py-3 text-right font-mono font-bold">{fmt(t.amount, t.currency)}</td>
                    <td className="px-5 py-3 text-right text-xs text-[#0F2537]/70">{t.completed_at ? new Date(t.completed_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Comp Pro grants -----------------------------------------------
       * Operator-only tool to gift permanent Bula Pro to a list of emails
       * (founders, sponsored players, comps for press). Wraps the existing
       * POST /admin/comp-pro endpoint. Permanent until manually revoked.
       */}
      <CompProPanel />
    </div>
  );
}

function Tile({ testid, icon: Icon, label, value, sub, accent }) {
  return (
    <div className="card-default p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={14} weight="fill" className={accent ? "text-[#E05D3A]" : "text-[#0F2537]/60"} />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className={`font-heading font-black text-2xl mt-1 ${accent ? "text-[#E05D3A]" : ""}`}>{value}</div>
      <div className="text-xs text-[#0F2537]/55 mt-1">{sub}</div>
    </div>
  );
}
